<template>
    <div class="tracings-page">
        <!-- Header -->
        <div class="page-header d-flex justify-content-between align-items-center">
            <div>
                <h2 class="mb-0">Gestión de Hallazgos SG-SP</h2>
                <p class="text-muted mb-0">Administración centralizada de hallazgos y planes de acción</p>
            </div>
            
        </div>

        <!-- Scroll Container -->
        <div class="scroll-container">
            <!-- Filtros Sticky -->
            <div class="filters-sticky">
                <filters-tracings
                    :selected-company-id="selectedCompanyId"
                    :loading="loadingFilters"
                    :show-filters="showFilters"
                    :status-options="statusDefinitions"
                    :priority-options="priorityDefinitions"
                    :origin-options="moduleOriginOptions"
                    @company-changed="handleCompanyChanged"
                    @filters-changed="handleFiltersChanged"
                    @toggle-filters="showFilters = !showFilters"
                    @clear-filters="handleClearFilters"
                    @loading-start="loadingFilters = true"
                    @loading-end="loadingFilters = false"
                />
            </div>

            <!-- Summary Cards Sticky -->
            <div v-if="selectedCompanyId && hasData" class="summary-sticky">
                <tracing-summary-cards :summary="summary" />
            </div>
            

            <div v-if="selectedCompanyId && validarPermisoComponente(['crear-plan-accion-manual'])"
            class="d-flex justify-content-end mb-2"
            >
                <b-button variant="primary" @click="showCreateManualModal = true">
                    <i class="fa fa-plus-circle mr-1"></i> Nuevo hallazgo
                </b-button>
            </div>

            <!-- Contenedor Principal de Resultados -->
            <b-overlay :show="loadingActionPlans" rounded="sm">
                <div v-if="selectedCompanyId">
                    <!-- Groups -->
                    <div v-if="hasData" class="groups-container">
                        <tracing-group-card
                            v-for="group in groups"
                            :key="group.module_origin"
                            :ref="`group-${group.module_origin}`"
                            :group="group"
                            :status-definitions="statusDefinitions"
                            :priority-definitions="priorityDefinitions"
                            :notification-frequency-definitions="notificationFrequencyDefinitions"
                            :workers="safeWorkers"
                            :company-id="selectedCompanyId"
                            :initial-expanded="expandedOrigin === group.module_origin"
                            @origin-toggled="handleOriginToggled"
                            @updated="silentRefresh"
                        />
                    </div>

                    <!-- Estado vacío con datos cargados -->
                    <b-card v-else-if="!loadingActionPlans" class="text-center py-5">
                        <i class="fa fa-inbox fa-3x text-muted mb-3"></i>
                        <h5 class="text-muted">No se encontraron planes de acción</h5>
                        <p class="text-muted">Intente modificar los filtros de búsqueda</p>
                    </b-card>
                </div>

                <!-- Estado Vacío Inicial -->
                <b-card v-else class="text-center py-5">
                    <i class="fa fa-building fa-3x text-muted mb-3"></i>
                    <h4 class="text-muted">
                        Seleccione una empresa para ver los hallazgos
                    </h4>
                </b-card>
            </b-overlay>
        </div>

        <!-- Modals -->
        <create-plan-action-manual-modal
            v-if="selectedCompanyId"
            :visible="showCreateManualModal"
            :company-id="selectedCompanyId"
            :workers="safeWorkers"
            :priority-options="priorityDefinitions"
            :status-options="statusDefinitions"
            @close="showCreateManualModal = false"
            @created="silentRefresh"
        />
    </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import FiltersTracings from "./components/FiltersTracings.vue";
import TracingGroupCard from "./components/TracingGroupCard.vue";
import TracingSummaryCards from "./components/TracingSummaryCards.vue";
import CreatePlanActionManualModal from "./components/CreatePlanActionManualModal.vue";
import { validarPermisoComponente } from "@/plugins/acl";

export default {
    name: "TracingsIndex",
    components: {
        FiltersTracings,
        TracingGroupCard,
        TracingSummaryCards,
        CreatePlanActionManualModal,
    },
    data() {
        return {
            selectedCompanyId: null,
            loadingFilters: false,
            loadingActionPlans: false,
            showFilters: false,
            showCreateManualModal: false,
            currentFilters: {
                status: ['Abierto', 'Proceso'],
            },
            expandedOrigin: null,
             validarPermisoComponente: validarPermisoComponente,
        };
    },
    methods: {
        ...mapActions("skudo/tracings", ["getAllActiveActionPlans"]),

        async handleCompanyChanged(companyId) {
            this.selectedCompanyId = companyId;
            if (!companyId) {
                this.currentFilters = { status: ['Abierto', 'Proceso'] };
                this.showFilters = false;
            } else {
                await this.fetchActionPlans();
            }
        },
        async handleFiltersChanged(filters) {
            this.currentFilters = filters;
            if (this.selectedCompanyId) {
                await this.fetchActionPlans();
            }
        },
        handleClearFilters() {
            this.currentFilters = { status: ['Abierto', 'Proceso'] };
            if (this.selectedCompanyId) {
                this.fetchActionPlans();
            }
        },
        async fetchActionPlans() {
            if (!this.selectedCompanyId) return;

            this.loadingActionPlans = true;
            try {
                await this.getAllActiveActionPlans({
                    company_id: this.selectedCompanyId,
                    ...this.currentFilters,
                });
            } catch (error) {
                console.error("Error fetching action plans:", error);
            } finally {
                this.loadingActionPlans = false;
            }
        },
        async silentRefresh() {
            if (!this.selectedCompanyId) return;
            try {
                await this.getAllActiveActionPlans({
                    company_id: this.selectedCompanyId,
                    ...this.currentFilters,
                });
            } catch (error) {
                console.error("Error refreshing action plans:", error);
            }
        },
        handleOriginToggled(moduleOrigin) {
            // Collapse all other origins when one is expanded
            if (moduleOrigin) {
                this.expandedOrigin = moduleOrigin;
                // Collapse other groups
                this.groups.forEach(group => {
                    if (group.module_origin !== moduleOrigin) {
                        const ref = this.$refs[`group-${group.module_origin}`];
                        if (ref && ref[0] && ref[0].collapseAll) {
                            ref[0].collapseAll();
                        }
                    }
                });
            } else {
                this.expandedOrigin = null;
            }
        },
    },
    computed: {
        ...mapState("skudo/tracings", ["allActiveActionPlans"]),
        ...mapState("trainings", ["workers"]),
        
        groups() {
            return (this.allActiveActionPlans && this.allActiveActionPlans.groups) || [];
        },
        summary() {
            return (this.allActiveActionPlans && this.allActiveActionPlans.summary) || {
                total_plans: 0,
                by_status: { abierto: 0, proceso: 0, cerrado: 0, cerrado_por_verificar: 0 },
                by_priority: { alto: 0, medio: 0, bajo: 0 },
            };
        },
        filterOptions() {
            return (this.allActiveActionPlans && this.allActiveActionPlans.filter_options) || {};
        },
        statusDefinitions() {
            return (this.filterOptions && this.filterOptions.statuses) || [];
        },
        priorityDefinitions() {
            return (this.filterOptions && this.filterOptions.priorities) || [];
        },
        moduleOriginOptions() {
            return (this.filterOptions && this.filterOptions.module_origins) || [];
        },
        notificationFrequencyDefinitions() {
            return (this.filterOptions && this.filterOptions.notification_frequencies) || [];
        },
        hasData() {
            return this.groups && this.groups.length > 0;
        },
        safeWorkers() {
            if (!this.workers || !this.workers.length) return [];
            return this.workers.map((worker) => ({
                ...worker,
                fullName: `${worker.names || ''} ${worker.surnames || ''}`.trim(),
            }));
        },
    },
};
</script>

<style scoped>
.tracings-page {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 120px);
    overflow: hidden;
}



.scroll-container {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
}

.filters-sticky {
    position: sticky;
    top: 0;
    z-index: 100;
    background: white;
    padding-bottom: 10px;
}

.summary-sticky {
    position: sticky;
    top: 99px;
    z-index: 99;
    background: white;
    padding-bottom: 10px;
}

@media (max-width: 768px) {
    .tracings-page {
        height: auto;
        overflow: visible;
    }
    
    .scroll-container {
        overflow: visible;
    }
    
    .filters-sticky,
    .summary-sticky {
        position: relative;
        top: auto;
    }
}
</style>
